import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Stack, Typography } from "@mui/material";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { useTheme } from "@mui/material/styles";
import { format } from "date-fns";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

interface AssessmentData {
  administration_date: string;
  adaptive_behavior_composite_standard_score?: number;
  communication_score?: number;
  daily_living_score?: number;
  socialization_score?: number;
  motor_score?: number;
}

interface Assessment {
  data: AssessmentData;
}

interface LinesGraphProps {
  dataAssessments: Assessment[];
}

const updateMonths = (dateString: string, months: number, isAdd: boolean): Date => {
  const date = new Date(dateString);
  const currentMonth = date.getMonth();
  
  if (isAdd) {
    date.setMonth(currentMonth + months);
  } else {
    date.setMonth(currentMonth - months);
  }
  
  return date;
};

const getMaxAndMin = (values: (number | null | undefined)[]): { max: number; min: number } => {
  const validValues = values.filter((v): v is number => v !== null && v !== undefined);
  
  if (validValues.length === 0) {
    return { max: 140, min: 0 };
  }
  
  const max = Math.max(...validValues);
  const min = Math.min(...validValues);
  
  return {
    max: Math.ceil(max / 20) * 20 + 20,
    min: Math.floor(min / 20) * 20 - 20
  };
};

const optionsGraph: any = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: "index",
    intersect: false,
  },
  stacked: false,
  scales: {
    y: {
      type: "linear",
      display: true,
      position: "left",
      border: {
        dash: [5, 5],
        color: "rgba(9, 26, 42, 1)",
      },
      grid: {
        tickLength: 0,
      },
      ticks: {
        padding: 8,
      },
    },
    x: {
      border: {
        dash: [5, 5],
        color: "rgba(9, 26, 42, 1)",
      },
      grid: {
        tickLength: 0,
      },
      ticks: {
        padding: 15,
      },
    },
  },
  plugins: {
    legend: {
      display: true,
      position: 'bottom',
      align: 'start',
      labels: {
        boxWidth: 8,
        boxHeight: 8,
        padding: 10,
        usePointStyle: true,
        pointStyle: 'circle',
      },
    },
    tooltip: {
      usePointStyle: true,
      padding: 8,
      backgroundColor: "rgba(0, 0, 0, 1)",
      boxPadding: 2,
      boxWidth: 7,
      boxHeight: 7,
      bodyFont: {
        LineHeight: 14,
      },
    },
  },
};

const LinesGraph: React.FC<LinesGraphProps> = ({ dataAssessments }) => {
  const theme = useTheme();
  const { t } = useTranslation(["clientHeader"]);

  const ABS_LINE = {
    label: t("clientHeader:assessments.adaptiveBehaviorComposite"),
    borderColor: "rgb(142, 66, 202)",
    backgroundColor: "rgba(142, 66, 202, 0.5)",
  };

  const COMMUNICATION_LINE = {
    label: t("clientHeader:assessments.communicationScore"),
    borderColor: "rgb(255, 193, 7)",
    backgroundColor: "rgba(255, 193, 7, 0.5)",
  };

  const DAILY_LIVING_LINE = {
    label: t("clientHeader:assessments.dailyLivingScore"),
    borderColor: "rgb(244, 67, 54)",
    backgroundColor: "rgba(244, 67, 54, 0.5)",
  };

  const SOCIALIZATION_LINE = {
    label: t("clientHeader:assessments.socializationScore"),
    borderColor: "rgb(76, 175, 80)",
    backgroundColor: "rgba(76, 175, 80, 0.5)",
  };

  const MOTOR_SKILLS_LINE = {
    label: t("clientHeader:assessments.motorSkills"),
    borderColor: "rgb(33, 150, 243)",
    backgroundColor: "rgba(33, 150, 243, 0.5)",
  };


  const initDate = updateMonths(dataAssessments[0].data.administration_date, 6, false);
  const endDate = updateMonths(dataAssessments[dataAssessments.length - 1].data.administration_date, 6, true);

  const labels: string[] = [format(initDate, "MM/dd/yy")];

  const ABS_data: (number | null)[] = [null];
  const CL_data: (number | null)[] = [null];
  const DL_data: (number | null)[] = [null];
  const SO_data: (number | null)[] = [null];
  const MS_data: (number | null)[] = [null];

  const valuesY: (number | null | undefined)[] = [];

  dataAssessments.forEach((assessment) => {
    const fullDate = new Date(assessment.data.administration_date);
    const shortLabel = format(fullDate, "MM/dd/yy");
    
    labels.push(shortLabel);

    ABS_data.push(assessment.data.adaptive_behavior_composite_standard_score ?? null);
    CL_data.push(assessment.data.communication_score ?? null);
    DL_data.push(assessment.data.daily_living_score ?? null);
    SO_data.push(assessment.data.socialization_score ?? null);
    MS_data.push(assessment.data.motor_score ?? null);

    valuesY.push(
      assessment.data.adaptive_behavior_composite_standard_score,
      assessment.data.communication_score,
      assessment.data.daily_living_score,
      assessment.data.socialization_score,
      assessment.data.motor_score
    );
  });

  labels.push(format(endDate, "MM/dd/yy"));

  const dataLines = {
    labels,
    datasets: [
      { ...ABS_LINE, data: ABS_data },
      { ...COMMUNICATION_LINE, data: CL_data },
      { ...DAILY_LIVING_LINE, data: DL_data },
      { ...SOCIALIZATION_LINE, data: SO_data },
      { ...MOTOR_SKILLS_LINE, data: MS_data },
    ],
  };

  const { max, min } = getMaxAndMin(valuesY);

  const chartOptions = {
    ...optionsGraph,
    scales: {
      ...optionsGraph.scales,
      y: { ...optionsGraph.scales.y, suggestedMax: max, suggestedMin: min },
    },
  };

  return (
    <Stack sx={{ mt: theme.spacing(10) }}>
      <Box component="section" sx={{ height: 270, marginBottom: theme.spacing(5) }}>
        <Line options={chartOptions} data={dataLines} />
      </Box>
      
      <Box>
        <Typography variant="subtitle1">{t("clientHeader:assessments.abcDomainScoreProfile")}</Typography>
        <Typography variant="body2">
          {t("clientHeader:assessments.meanSdNote")}
        </Typography>
      </Box>
    </Stack>
  );
};

export default LinesGraph;
